interface AnimalSettingLike {
  species?: string | null;
  schedulingDeposit?: number | null;
}

/**
 * Per-species scheduling deposit (per head) from a processor's animalSettings,
 * matched case-insensitively. Unset ⇒ 0 — the legacy top-level
 * `schedulingDeposit` is deliberately ignored; per-species is the only source
 * of truth (mirrors the API's `resolveSpeciesDeposit`).
 * @param animalSettings
 * @param species
 */
export function speciesDeposit(
  animalSettings:
    | ReadonlyArray<AnimalSettingLike | null | undefined>
    | null
    | undefined,
  species: string | null | undefined,
): number {
  if (!species) return 0;
  const match = (animalSettings ?? []).find(
    (a) => a?.species?.toLowerCase() === species.toLowerCase(),
  );
  return match?.schedulingDeposit ?? 0;
}

/**
 * True when any species takes a deposit (used to gate the deposit step).
 * @param animalSettings
 */
export function anySpeciesHasDeposit(
  animalSettings:
    | ReadonlyArray<AnimalSettingLike | null | undefined>
    | null
    | undefined,
): boolean {
  return (animalSettings ?? []).some((a) => (a?.schedulingDeposit ?? 0) > 0);
}

interface ProcessorSettingsLike {
  animalSettings?: ReadonlyArray<AnimalSettingLike | null | undefined> | null;
  vendor?: { stripe_config?: { id?: string | null } | null } | null;
}

/**
 * Whether the final Deposit step applies for a given processor + species: the
 * booked species must take a non-zero deposit. Used to show/hide the Deposit
 * step live as the user changes processor or species.
 *
 * NOTE: this deliberately does NOT check `vendor.stripe_config.id`. That field
 * is owner-gated in the API (it holds the Stripe account + a client secret), so
 * it resolves to `null` for a producer booking at someone else's processor — the
 * exact user this wizard serves. Gating on it hid the Deposit step for every
 * producer. The connected account is obtained server-side when the Deposit step
 * mints the PaymentIntent (`bookingDepositPaymentIntentCreate`), which fails
 * gracefully (retry UI, no charge) if the processor can't actually collect —
 * matching how the buyer/on-behalf flows already work.
 * @param processorSettings
 * @param species
 */
export function isDepositApplicable(
  processorSettings: ProcessorSettingsLike | null | undefined,
  species: string | null | undefined,
): boolean {
  if (!processorSettings) return false;
  return speciesDeposit(processorSettings.animalSettings, species) > 0;
}
